// src/components/CourseDetail.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchCourses } from '../services/courseService';
import BookingForm from './BookingForm';
import SmartWaitlistForm from './SmartWaitlistForm';

export default function CourseDetail() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  useEffect(() => {
    async function loadCourse() {
      try {
        // Fetch all courses and find the one with matching id
        const allCourses = await fetchCourses();
        const foundCourse = allCourses.find(c => c.id === id);
        
        if (foundCourse) {
          setCourse(foundCourse);
        } else {
          setError('Course not found.');
        }
      } catch (err) {
        console.error('Error loading course:', err);
        setError('Failed to load course details.');
      } finally {
        setLoading(false);
      }
    }
    loadCourse();
  }, [id]);

  if (loading) return <div className="text-center py-8">Loading course details...</div>;
  if (error) return <div className="text-center text-red-600 py-8">{error}</div>;
  if (!course) return <div className="text-center text-red-600 py-8">Course not found</div>;

  const handleBookingSuccess = () => {
    setBookingSuccess(true);
    // Update available spots
    setCourse(prev => ({
      ...prev,
      availableSpots: prev.availableSpots - 1
    }));
  };

  const handleWaitlistSuccess = () => {
    setWaitlistSuccess(true);
  };

  const showBookingForm = course.availableSpots > 0 && !bookingSuccess;
  const showWaitlistForm = course.availableSpots === 0 && !waitlistSuccess;

  return (
    <div className="max-w-3xl mx-auto my-8 p-4 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">{course.title}</h2>
      <p className="text-gray-600 mb-4">{course.description}</p>
      
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div>
          <p><strong>Start Date:</strong> {course.startDate}</p>
          <p><strong>End Date:</strong> {course.endDate}</p>
        </div>
        <div>
          <p><strong>Total Spots:</strong> {course.totalSpots}</p>
          <p><strong>Available Spots:</strong> {course.availableSpots}</p>
        </div>
      </div>
      
      {bookingSuccess && (
        <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg">
          <p className="font-bold">Booking successful!</p>
          <p>You have secured your spot in this course.</p>
        </div>
      )}
      
      {waitlistSuccess && (
        <div className="mb-6 p-4 bg-blue-100 text-blue-700 rounded-lg">
          <p className="font-bold">Successfully joined the waitlist!</p>
          <p>We'll notify you when a spot becomes available.</p>
        </div>
      )}
      
      {showBookingForm && <BookingForm courseId={id} onBookingSuccess={handleBookingSuccess} />}
      
      {showWaitlistForm && <SmartWaitlistForm courseId={id} onWaitlistSuccess={handleWaitlistSuccess} />}
      
      {!showBookingForm && !showWaitlistForm && !bookingSuccess && !waitlistSuccess && (
        <div className="p-4 bg-yellow-100 text-yellow-700 rounded-lg">
          <p>This course is currently unavailable for booking or waitlisting.</p>
        </div>
      )}
    </div>
  );
}